import React, { useContext } from "react";
import { makeGet } from "@culturehq/client";

import { usePagedEntity } from "app/hooks";

import * as CHQ from "chq";
import { useProfile } from "./ProfileContext";
import { usePublic } from "./PublicContext";

type InterestsState = {
  allInterestsFetched: boolean;
  onAllInterestsRequested: () => void;
  onAutocompleteInterests: (_query: string) => void;
  onNextInterestPageRequested: () => void;
  onInterestsRequested: (_interestIds: number[]) => void;
  onAddInterest: (_interest: CHQ.Interest) => void;
  interests: { [key: number]: CHQ.Interest };
};

const InterestsContext = React.createContext<InterestsState>({
  allInterestsFetched: false,
  onAllInterestsRequested: () => {},
  onAutocompleteInterests: () => {},
  onNextInterestPageRequested: () => {},
  onInterestsRequested: () => {},
  onAddInterest: () => {},
  interests: {}
});

const onFetchPage = (
  isPublic: boolean, publicQuestionId: number) => (page: number) => makeGet(
  `${isPublic ? "/public/interests" : "/interests"}`, { page, id: publicQuestionId });
const onFetchEntity = (interestId: number) => makeGet(`/interests/${interestId}`);
const onFetchAutocomplete = (query: string) => makeGet("/autocomplete/interests", { query });

export const InterestsProvider: React.FC = ({ children }) => {
  const { isPublic } = useProfile();
  const { questionId: publicQuestionId = 0 } = usePublic();
  const value = usePagedEntity(
    "interest",
    onFetchPage(isPublic, publicQuestionId),
    onFetchEntity,
    onFetchAutocomplete
  );

  return (
    <InterestsContext.Provider value={value as InterestsState}>
      {children}
    </InterestsContext.Provider>
  );
};

export const useInterests = () => useContext(InterestsContext);
